import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Select, message, Spin, Switch } from 'antd';
import axiosInstance from '../../axiosInstance';
import { retailerId } from '../constants';

const { Option } = Select;

const ShopConnections = () => {
    const [shops, setShops] = useState([]);
    const [connections, setConnections] = useState([]);
    const [currentShopId, setCurrentShopId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [selectedConnections, setSelectedConnections] = useState([]);
    const [shareProducts, setShareProducts] = useState({});
    const [unlinkId, setUnlinkId] = useState(null); // NEW: Store id for unlink confirmation
    const [isUnlinkModalVisible, setIsUnlinkModalVisible] = useState(false); // NEW: Control unlink modal

    const [form] = Form.useForm();

    useEffect(() => {
        fetchAllShops();
    }, []);

    useEffect(() => {
        if (currentShopId) {
            fetchConnections();
        }
    }, [currentShopId]);

    const fetchAllShops = async () => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get('/shops');
            setShops(data.shops || []);
        } catch (error) {
            message.error('Failed to load shops.');
        } finally {
            setLoading(false);
        }
    };

    const fetchConnections = async () => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get('/shops/connections', {
                params: { shopId: currentShopId },
            });
            setConnections(data.connections || []);
        } catch (error) {
            message.error('Failed to load connections.');
        } finally {
            setLoading(false);
        }
    };

    const openLinkModal = async () => {
        try {
          const { data } = await axiosInstance.get('/shops', {
            params: { retailerId },
          });
      
          const allShops = data.shops.filter(shop => shop.id !== currentShopId);
      
          // Exclude already connected shops
          const connectedIds = connections.map(conn => conn.id);
          const filteredShops = allShops.filter(shop => !connectedIds.includes(shop.id));
      
          setAvailableShops(filteredShops);
          setIsModalVisible(true);
        } catch (error) {
          message.error('Failed to load available shops.');
        }
      };
      


    const handleLink = async () => {
        const payload = {
            shopId: currentShopId,
            connections: selectedConnections.map(id => ({
                id,
                share_products: shareProducts[id] || false,
            })),
        };

        try {
            await axiosInstance.post('/shops/link', payload);
            message.success('Shops linked successfully.');
            setIsModalVisible(false);
            form.resetFields();
            fetchConnections();
        } catch (error) {
            message.error('Failed to link shops.');
        }
    };

    const confirmUnlink = (id) => {
        setUnlinkId(id);
        setIsUnlinkModalVisible(true);
    };

    const handleUnlink = async () => {
        if (!unlinkId) return;

        try {
            await axiosInstance.post('/shops/unlink', {
                shopId: currentShopId,
                disconnect_id: unlinkId,
            });
            message.success('Shop disconnected.');
            fetchConnections();
        } catch (error) {
            message.error('Failed to unlink shop.');
        } finally {
            setIsUnlinkModalVisible(false);
            setUnlinkId(null);
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Share Products',
            dataIndex: 'share_products',
            key: 'share_products',
            render: (share_products) => (share_products === 'yes' ? 'Yes' : 'No'),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button danger onClick={() => confirmUnlink(record.id)}>
                    Unlink
                </Button>
            ),
        },
    ];

    return (
        <div>
            <h2>Shop Connections Settings</h2>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
                <Select
                    placeholder="Select your shop"
                    style={{ flex: '1 1 250px', minWidth: 200 }}
                    value={currentShopId}
                    onChange={(value) => setCurrentShopId(value)}
                >
                    {shops.map((shop) => (
                        <Option key={shop.id} value={shop.id}>
                            {shop.name}
                        </Option>
                    ))}
                </Select>

                <Button
                    type="primary"
                    onClick={openLinkModal}
                    disabled={!currentShopId}
                    style={{ flex: '0 1 auto', minWidth: 200 }}
                >
                    Link Shops / Warehouses
                </Button>
            </div>

            <Spin spinning={loading}>
                <Table
                    columns={columns}
                    dataSource={connections}
                    rowKey="id"
                    pagination={{ pageSize: 25 }}
                    scroll={{ x: 'max-content' }}
                    style={{ marginTop: 24 }}
                />
            </Spin>

            {/* Link Modal */}
            <Modal
                title="Link Shops or Warehouses"
                visible={isModalVisible}
                onOk={() => form.submit()}
                onCancel={() => setIsModalVisible(false)}
                okText="Link"
                destroyOnClose
            >
                <Form form={form} onFinish={handleLink} layout="vertical">
                    <Form.Item
                        label="Select Shops/Warehouses"
                        name="connections"
                        initialValue={selectedConnections}
                        rules={[{ required: true, message: 'Please select at least one shop!' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select shops to link"
                            value={selectedConnections}
                            onChange={setSelectedConnections}
                            options={availableShops.map(shop => ({
                                label: shop.name,
                                value: shop.id,
                            }))}
                        />
                    </Form.Item>

                    {selectedConnections.length > 0 && (
                        <div>
                            <h3>Share Products</h3>
                            {selectedConnections.map((id) => (
                                <Form.Item
                                    key={id}
                                    label={`Share Products with ${availableShops.find(shop => shop.id === id)?.name}`}
                                >
                                    <Switch
                                        checked={shareProducts[id] || false}
                                        onChange={(checked) => {
                                            setShareProducts(prevState => ({
                                                ...prevState,
                                                [id]: checked,
                                            }));
                                        }}
                                    />
                                </Form.Item>
                            ))}
                        </div>
                    )}
                </Form>
            </Modal>

            {/* Unlink Confirmation Modal */}
            <Modal
                title="Confirm Unlink"
                visible={isUnlinkModalVisible}
                onOk={handleUnlink}
                onCancel={() => setIsUnlinkModalVisible(false)}
                okText="Unlink"
                okButtonProps={{ danger: true }}
                destroyOnClose
            >
                <p>Are you sure you want to unlink this shop/warehouse?</p>
            </Modal>
        </div>
    );
};

export default ShopConnections;
